/**
 * Domain vocabulary for CenoX Business.
 *
 * These are the *only* definitions of these values in the system. The API's
 * Postgres enums, the API's Zod validators and the dashboard's dropdowns are
 * all derived from here, so the three can never drift apart.
 *
 * Adding a value: append it, add a migration with `ALTER TYPE ... ADD VALUE`,
 * and add a label below. Never reorder or delete — the strings are persisted.
 */

// ── Business ────────────────────────────────────────────────────────────────

export const BUSINESS_TYPES = [
  'restaurant',
  'water_production',
  'supermarket_retail',
  /** Shown as "Delivery & Logistics". The stored key stays `logistics`. */
  'logistics',
  'hotel',
  'salon',
  'professional_services',
  'manufacturing',
  'healthcare',
  'other',
  /**
   * A workspace that is not a shop.
   *
   * A customer buying only CenoX Automation or CenoX Robotics still needs a
   * workspace — billing, members, audit, and the tenant id every row level
   * security policy reads — but has no kind of shop.
   *
   * Emphatically not `other`, which means "a shop of a kind we did not list".
   * Reusing it would seed a robotics company a shop's expense categories and
   * file it in the retail bucket of every report that groups by type. A fake
   * answer is worse than an honest absence.
   *
   * Never offered in onboarding — see SELECTABLE_BUSINESS_TYPES.
   */
  'platform',
  /**
   * A pharmacy or chemist: medicines, cosmetics and ordinary retail together.
   *
   * Appended, not inserted, because the enum is persisted. Promoted from the
   * `pharmacy` preset over `supermarket_retail`, which could name the
   * catalogue "Medicines" but could not switch on batches, expiry,
   * prescriptions or a dispensing register — those are the Pharmacy module,
   * and a type is what decides a module's default. Existing businesses on the
   * preset keep their type; the module is switched on for them explicitly.
   */
  'pharmacy',
] as const;
export type BusinessType = (typeof BUSINESS_TYPES)[number];

/**
 * The types a person may actually choose.
 *
 * `platform` is set by the system when a workspace is created without a
 * product that needs one; it is not a shop somebody picks from a list.
 */
export const SELECTABLE_BUSINESS_TYPES = [
  'restaurant',
  'water_production',
  'supermarket_retail',
  'logistics',
  'hotel',
  'salon',
  'professional_services',
  'manufacturing',
  'healthcare',
  'pharmacy',
  'other',
] as const satisfies readonly Exclude<BusinessType, 'platform'>[];

/**
 * Adding a business type and forgetting to offer it is a silent bug: the type
 * exists, nothing rejects it, and it simply never appears in onboarding. This
 * line fails to compile instead.
 */
type SelectableCoversEveryType =
  Exclude<BusinessType, 'platform'> extends (typeof SELECTABLE_BUSINESS_TYPES)[number]
    ? true
    : ['a business type is missing from SELECTABLE_BUSINESS_TYPES'];
const _selectableCoversEveryType: SelectableCoversEveryType = true;
void _selectableCoversEveryType;

export const BUSINESS_TYPE_LABELS: Record<BusinessType, string> = {
  restaurant: 'Restaurant',
  water_production: 'Water Production',
  supermarket_retail: 'Supermarket / Retail',
  logistics: 'Delivery & Logistics',
  hotel: 'Hotel',
  salon: 'Salon',
  professional_services: 'Professional Services',
  manufacturing: 'Manufacturing',
  healthcare: 'Healthcare',
  other: 'Other',
  platform: 'CenoX Workspace',
  pharmacy: 'Pharmacy',
};

// ── Roles ───────────────────────────────────────────────────────────────────

/** Ordered least- to most-privileged. Index is used for "at least" comparisons. */
/**
 * Ordered least to most authority. `canManageRole` reads the ORDER, so a role's
 * position is a statement about who may act on whom, not a label.
 *
 * `location_operator` sits at the bottom deliberately. It is not a junior
 * person — it is a shared counter credential bound to one location, and it must
 * be able to manage nobody at all. Its own screen is Settings → Locations →
 * Login & Access, and `ASSIGNABLE_MEMBER_ROLES` keeps it out of the Team role
 * picker, because a location login is created with a location and a password
 * rather than promoted into existence.
 */
export const MEMBER_ROLES = [
  /**
   * A machine acting for one business, through an integration credential.
   *
   * First in the list because the order is the privilege order — `roleAtLeast`
   * and `canManageRole` read the index — and an integration must sit below
   * everything a person can be. Nobody can be given this role on the Team
   * screen; it arrives only with a credential.
   */
  'integration',
  'location_operator',
  'worker',
  'staff',
  'admin',
  'owner',
] as const;
export type MemberRole = (typeof MEMBER_ROLES)[number];

export const MEMBER_ROLE_LABELS: Record<MemberRole, string> = {
  worker: 'Worker',
  owner: 'Owner',
  admin: 'Admin',
  staff: 'Staff',
  location_operator: 'Location login',
  integration: 'Integration',
};

/**
 * What kind of thing is signing in.
 *
 * `personal` is a human being with their own account, who may belong to several
 * businesses and whose record follows them between them. `location` is a shared
 * counter login created by an owner, bound to one location of one business for
 * its whole life.
 *
 * Stored on the user rather than inferred from the role, because the two answer
 * different questions: the role says what it may do, the kind says what it IS —
 * and the rules that make a location account safe (one membership, one
 * location, no self-service password reset, no invitation flow) all hang off
 * the second.
 */
export const ACCOUNT_KINDS = ['personal', 'location', 'integration'] as const;
export type AccountKind = (typeof ACCOUNT_KINDS)[number];

/**
 * Roles a PERSON can be given on the Team screen.
 *
 * `location_operator` is absent: it belongs to a location, not a person, and is
 * created with a username, a password and a binding the member editor knows
 * nothing about. `integration` is absent for the same reason — it belongs to a
 * credential, and arrives only when one is issued.
 */
export const ASSIGNABLE_MEMBER_ROLES: readonly MemberRole[] = MEMBER_ROLES.filter(
  (role) => role !== 'location_operator' && role !== 'integration',
);

export const MEMBER_STATUSES = ['active', 'invited', 'suspended'] as const;
export type MemberStatus = (typeof MEMBER_STATUSES)[number];

// ── Sales ───────────────────────────────────────────────────────────────────

export const SALE_SOURCES = [
  'manual',
  'whatsapp',
  'app',
  'website',
  'pos',
  'api',
] as const;
export type SaleSource = (typeof SALE_SOURCES)[number];

/**
 * Which system a sale originated in — not how it was sold.
 *
 * `SALE_SOURCES` above is the channel, and mirrors the channel catalogue
 * one-for-one. A sale exported from Microsoft 365 was still made over one of
 * those channels, so recording its provenance there would overwrite the fact
 * that field exists to record.
 *
 * Deliveries learned this the same way: `origin_source` says Shop, App or
 * WhatsApp, and `record_origin` says whether CenoX watched it happen or read
 * it from a sheet. Two questions, two columns.
 */
export const SALE_SOURCE_SYSTEMS = ['cenox', 'microsoft_365'] as const;
export type SaleSourceSystem = (typeof SALE_SOURCE_SYSTEMS)[number];

export const SALE_SOURCE_SYSTEM_LABELS: Record<SaleSourceSystem, string> = {
  cenox: 'Entered in CenoX',
  microsoft_365: 'Microsoft 365',
};

export const SALE_SOURCE_LABELS: Record<SaleSource, string> = {
  manual: 'Manual',
  whatsapp: 'WhatsApp',
  app: 'App',
  website: 'Website',
  pos: 'POS',
  api: 'API',
};

export const PAYMENT_METHODS = [
  'cash',
  'bank_transfer',
  'card',
  'mobile_money',
  'pos_terminal',
  'cheque',
  'credit',
  'other',
] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  cash: 'Cash',
  bank_transfer: 'Bank Transfer',
  card: 'Card',
  mobile_money: 'Mobile Money',
  pos_terminal: 'POS Terminal',
  cheque: 'Cheque',
  credit: 'Credit',
  other: 'Other',
};

export const PAYMENT_STATUSES = [
  'paid',
  'partial',
  'unpaid',
  'refunded',
  'void',
] as const;
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];

export const PAYMENT_STATUS_LABELS: Record<PaymentStatus, string> = {
  paid: 'Paid',
  partial: 'Partially Paid',
  unpaid: 'Unpaid',
  refunded: 'Refunded',
  void: 'Void',
};

/**
 * Statuses that represent money the business still expects to receive.
 * Used by the dashboard's "outstanding payments" tile and the reports module.
 */
export const OUTSTANDING_STATUSES: readonly PaymentStatus[] = ['unpaid', 'partial'];

/** Statuses excluded from revenue totals — a void sale never happened. */
export const NON_REVENUE_STATUSES: readonly PaymentStatus[] = ['void'];

// ── Catalogue ───────────────────────────────────────────────────────────────

/**
 * A catalogue entry is either something you hand over or something you do.
 *
 * The distinction is not cosmetic: a service has no stock to deplete, so
 * `track_stock` is meaningless on it, and a report that showed "0 in stock"
 * against a haircut would be actively misleading.
 */
export const PRODUCT_KINDS = ['product', 'service'] as const;
export type ProductKind = (typeof PRODUCT_KINDS)[number];

export const PRODUCT_KIND_LABELS: Record<ProductKind, string> = {
  product: 'Product',
  service: 'Service',
};

export const PRODUCT_AVAILABILITIES = ['available', 'out_of_stock', 'discontinued'] as const;
export type ProductAvailability = (typeof PRODUCT_AVAILABILITIES)[number];

export const PRODUCT_AVAILABILITY_LABELS: Record<ProductAvailability, string> = {
  available: 'Available',
  out_of_stock: 'Out of stock',
  discontinued: 'Discontinued',
};

/**
 * Why stock moved. Every change to on-hand quantity carries one of these and a
 * source, so a count can always be explained rather than merely asserted.
 */
export const STOCK_MOVEMENT_KINDS = [
  'opening',
  'receipt',
  'sale',
  'return',
  'adjustment',
  'wastage',
  /**
   * Stock moved from one location to another.
   *
   * Always two rows: one negative where it left, one positive where it
   * arrived, sharing a `transferGroupId`. Zero-sum for the business, which is
   * why it is not a receipt — receiving raises what the business owns, and
   * moving it between shelves never does.
   */
  'transfer',
] as const;
export type StockMovementKind = (typeof STOCK_MOVEMENT_KINDS)[number];

export const STOCK_MOVEMENT_KIND_LABELS: Record<StockMovementKind, string> = {
  opening: 'Opening balance',
  receipt: 'Stock received',
  sale: 'Sold',
  return: 'Returned',
  adjustment: 'Manual adjustment',
  wastage: 'Damaged or expired',
  transfer: 'Moved between locations',
};

/**
 * What to do when a sale would take stock below zero.
 *
 * `warn` is the default and the only sane one to start with: stock counts
 * drift constantly in real shops — sales get entered late, deliveries get
 * recorded the next morning — and a system that refuses the sale is a system
 * that gets worked around with pen and paper, which loses the record entirely.
 *
 * `block` and `ignore` exist for businesses that genuinely want them. A
 * pharmacy dispensing controlled stock may want `block`; a services business
 * that never counts anything may want `ignore`.
 */
export const STOCK_POLICIES = ['warn', 'block', 'ignore'] as const;
export type StockPolicy = (typeof STOCK_POLICIES)[number];

export const STOCK_POLICY_LABELS: Record<StockPolicy, string> = {
  warn: 'Allow, but warn me',
  block: 'Refuse the sale',
  ignore: 'Do not check stock',
};

// ── Business shape ──────────────────────────────────────────────────────────

export const OFFERING_KINDS = ['products', 'services', 'both'] as const;
export type OfferingKind = (typeof OFFERING_KINDS)[number];

export const OFFERING_KIND_LABELS: Record<OfferingKind, string> = {
  products: 'Products',
  services: 'Services',
  both: 'Both products and services',
};

export const SHOP_PRESENCES = ['physical', 'online', 'both'] as const;
export type ShopPresence = (typeof SHOP_PRESENCES)[number];

export const SHOP_PRESENCE_LABELS: Record<ShopPresence, string> = {
  physical: 'A physical shop or location',
  online: 'Online only',
  both: 'Both physical and online',
};

// ── Staff ───────────────────────────────────────────────────────────────────

export const EMPLOYMENT_STATUSES = ['active', 'on_leave', 'terminated'] as const;
export type EmploymentStatus = (typeof EMPLOYMENT_STATUSES)[number];

export const EMPLOYMENT_STATUS_LABELS: Record<EmploymentStatus, string> = {
  active: 'Active',
  on_leave: 'On Leave',
  terminated: 'Terminated',
};

// ── Reports ─────────────────────────────────────────────────────────────────

export const REPORT_PERIODS = [
  'today',
  'daily',
  'weekly',
  'monthly',
  'yearly',
  'custom',
] as const;
export type ReportPeriod = (typeof REPORT_PERIODS)[number];

export const REPORT_PERIOD_LABELS: Record<ReportPeriod, string> = {
  today: 'Today',
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
  yearly: 'Yearly',
  custom: 'Custom range',
};

/**
 * How each period describes the window it compares against.
 *
 * Growth is always measured against the *equivalent* previous window — this
 * week against last week, not against "the previous 7 days ending yesterday".
 * A business owner comparing Monday-to-Sunday against Monday-to-Sunday is
 * comparing like with like; a rolling window silently mixes weekends into
 * weekdays and makes every figure move for no reason.
 */
export const REPORT_COMPARISON_LABELS: Record<ReportPeriod, string> = {
  today: 'vs yesterday',
  daily: 'vs previous period',
  weekly: 'vs previous period',
  monthly: 'vs previous period',
  yearly: 'vs previous period',
  custom: 'vs preceding range of equal length',
};

/** Periods whose series is bucketed by hour rather than by calendar date. */
export const HOURLY_PERIODS: readonly ReportPeriod[] = ['today'];

// ── Sales channels ──────────────────────────────────────────────────────────

/**
 * The channels every new business starts with.
 *
 * These mirror `SALE_SOURCES` one-for-one so that existing sales, which carry
 * only the enum, map onto a real channel row without a data migration guess.
 * They are *seeds*, not a fixed list: a business can rename them, deactivate
 * the ones it does not use, and add its own — CenoX is multi-business SaaS and
 * a water distributor's channels are not a salon's.
 */
export const DEFAULT_SALES_CHANNELS: ReadonlyArray<{
  slug: SaleSource;
  name: string;
  sortOrder: number;
}> = [
  { slug: 'manual', name: 'Manual entry', sortOrder: 0 },
  { slug: 'whatsapp', name: 'WhatsApp', sortOrder: 1 },
  { slug: 'app', name: 'App', sortOrder: 2 },
  { slug: 'website', name: 'Website', sortOrder: 3 },
  { slug: 'pos', name: 'POS / Shop', sortOrder: 4 },
  { slug: 'api', name: 'API', sortOrder: 5 },
];

/** Dimensions a report figure can be broken down by, and drilled into. */
export const REPORT_DIMENSIONS = [
  'channel',
  'branch',
  'product',
  'paymentMethod',
  'paymentStatus',
  'customer',
  'staff',
  'expenseCategory',
] as const;
export type ReportDimension = (typeof REPORT_DIMENSIONS)[number];

export const REPORT_DIMENSION_LABELS: Record<ReportDimension, string> = {
  channel: 'Channel',
  branch: 'Branch',
  product: 'Product / service',
  paymentMethod: 'Payment method',
  paymentStatus: 'Payment status',
  customer: 'Customer',
  staff: 'Staff member',
  expenseCategory: 'Expense category',
};

/** Dimensions that describe expenses rather than sales. */
export const EXPENSE_DIMENSIONS: readonly ReportDimension[] = ['expenseCategory'];

export const EXPORT_FORMATS = ['csv', 'xlsx'] as const;
export type ExportFormat = (typeof EXPORT_FORMATS)[number];

// ── Audit ───────────────────────────────────────────────────────────────────

export const AUDIT_ACTIONS = [
  'create',
  'update',
  'delete',
  'void',
  'restore',
  'login',
  'logout',
  'invite',
  'role_change',
  'export',
] as const;
export type AuditAction = (typeof AUDIT_ACTIONS)[number];

/**
 * Entities whose changes are recorded in the audit log. Financial records are
 * mandatory; the rest are recorded because they change who can touch money.
 */
export const AUDIT_ENTITIES = [
  'sale',
  'sale_payment',
  'expense',
  'expense_category',
  'customer',
  'staff',
  'branch',
  'sales_channel',
  'product',
  'product_category',
  'stock_movement',
  'business',
  'business_member',
  'api_key',
  /**
   * A server-to-server credential.
   *
   * Auditable in its own right because it acts on its own: the trail has to say
   * who issued it, who rotated it and who stopped it, and those are the only
   * moments a person is involved at all.
   */
  'integration_credential',
  'user',
  'report',
  /**
   * An import batch. Auditable in its own right because committing or rolling
   * one back moves more records at once than any other action in the product,
   * and "who imported three years of sales, and who reversed it" is exactly
   * the question the trail exists to answer.
   */
  'import_batch',
  /**
   * A rider's commission rate.
   *
   * Ending one decides what every future delivery of that rider costs — closing
   * a single ₦1,000 arrangement moves ₦100,000 across the staging corpus — so
   * who changed it and when is worth keeping.
   */
  'rider_rate',
  /*
    Reserved for project sharing and membership.

    Declared with nothing writing it. Sharing a project makes one person's
    threads readable by another, which is an access decision and belongs in the
    trail; renaming a folder does not, and neither does a conversation, which
    is why no other AI artefact appears here.
  */
  'ai_project',
  /**
   * The default remittance for a kind of vehicle.
   *
   * One row decides what every driver on that category owes, which is a larger
   * decision than any single rider's rate. Changes supersede rather than edit,
   * so the trail already holds the old figure — this records who moved it, from
   * when, and what it replaced.
   */
  'vehicle_remittance_rate',
  /**
   * A delivery deadline.
   *
   * Not a `delivery_price_rule`: one decides what a job costs and the other
   * decides when it is late. Reusing the price rule's entity would have made
   * the trail say somebody changed a price when they changed a clock.
   */
  'delivery_sla_rule',
  /**
   * A settlement with a rider.
   *
   * The statement a rider is paid against, and paying one moves money out of
   * the business — so who built it, who paid it and against what reference is
   * exactly what the trail is for.
   */
  'rider_settlement',
  /**
   * A delivery job from a logistics business.
   *
   * Audited because applying one writes revenue and a rider's payout in the
   * same breath, and because the event that produced it came from another
   * system — "who recorded this, and what did the other side actually send"
   * is exactly what the trail has to answer.
   */
  'delivery',
  // A physical count of the drawer. Audited so a shortage appears in the same
  // timeline as the voids and stock adjustments from that day — a variance
  // only means something next to what else happened.
  'cash_count',
  // A physical stock count. Auditable because approving one rewrites the
  // ledger — "who decided the shelf held eight" is exactly the question the
  // trail exists to answer.
  'stock_count',
  // Who the goods came from. Auditable because a supplier's name is attached
  // to every delivery it brought, and renaming or retiring one changes how
  // past receipts read.
  'supplier',
  // A delivery note. Audited because posting one is the act that raises stock
  // — "who signed for forty cartons, and against whose invoice" is precisely
  // what the trail exists to answer.
  'goods_receipt',
  /**
   * A bike on the register.
   *
   * Audited because it is the most valuable thing the business hands to one
   * person: reassigning it moves an asset into somebody's custody, and
   * changing its status to `sold` or `repossessed` is a claim about where it
   * went. "Who gave CH-204-ABC to whom, and when" is the question.
   */
  'bike',
  /**
   * Taking in a rider's weekly remittance, or waiving it.
   *
   * Both are audited and for different reasons: a payment is cash somebody
   * received on the company's behalf, and a waiver forgives revenue the
   * company has already recognised.
   */
  'remittance_payment',
  /**
   * Money a rider handed over, split across weeks; a change to what a week
   * charges; and a rider's or vehicle's own agreed figure. Each is a decision
   * or a movement of money somebody has to be able to account for later.
   */
  'remittance_receipt',
  // 0140: investors and the remittance share that is theirs.
  'investor',
  'investor_payout',
  'bike_ownership',
  'investor_entitlement',
  // 0147: investments — vehicle partnerships, equity and profit shares — the
  // entities equity is held in, distributions declared, and payments made.
  'investment',
  'investment_entity',
  'investment_distribution',
  'investment_payment',
  // 0152: who received a delivery's money, and rider cash returns, payouts and adjustments.
  'delivery_collection',
  'rider_settlement_entry',
  // 0141: a rider's remittance cleared by hand.
  'remittance_debt_clearance',
  'remittance_adjustment',
  'remittance_agreement',
  /** Where money is held, and money moved between two of those places. */
  'money_account',
  'money_transfer',
  /**
   * A bike sold to a rider on instalments, and each repayment.
   *
   * Audited because the agreement sets what a rider owes for months, and
   * because a repayment reduces a receivable — misposting one as revenue is
   * precisely the mistake the separate ledgers exist to prevent, so the trail
   * has to show which ledger each payment landed in.
   */
  'hire_purchase_agreement',
  'hire_purchase_payment',
  /**
   * A loan, each entry on its ledger, and a rider's deduction agreement.
   *
   * Audited because a loan moves the business's cash into somebody's hands,
   * a reversal undoes a recorded payment, and a deduction agreement is the
   * written authority to take money out of a rider's remittance or pay.
   */
  'loan',
  'loan_transaction',
  'loan_deduction_agreement',
  'loan_charge',
  'lending_policy',
  /**
   * A location's own login.
   *
   * Audited more closely than a person's account, not less. It is a shared
   * credential written on a wall behind a counter, so "who created it", "who
   * reset the password", "when was it switched off" and "when did it last sign
   * in" are the whole of what can be known about who was using it.
   */
  'location_login',
  /**
   * A document in an AI project — for the security events only.
   *
   * Written by the processing job, naming no person: malware found, a file
   * that could not be scanned, the scanner itself unavailable. These happen
   * with nobody signed in, and are exactly what an owner investigating a bad
   * upload needs to find.
   */
  'ai_project_file',
  /**
   * Pharmacy. Clinical entities (`prescription`, `dispensing`, `patient`) are
   * audited with identifiers and changed field NAMES only — never allergies,
   * notes or directions in `before`/`after`. The trail proves who touched a
   * record without becoming a second copy of it that a non-clinical auditor
   * can read.
   */
  'pharmacy_medicine',
  'pharmacy_batch',
  'pharmacy_stock',
  'pharmacy_purchase',
  'pharmacy_supplier',
  'pharmacy_transfer',
  'pharmacy_sale',
  'pharmacy_refund',
  'pharmacy_payment',
  'pharmacy_till',
  'prescription',
  'dispensing',
  'patient',
  'restricted_register',
  'pharmacy_storage',
  'recall',
  'pharmacy_import',
  'pharmacy_settings',
  'pharmacy_staff',
  // 0158: a payment against a customer's opening balance.
  'opening_balance_payment',
  // 0165: what a bank statement said an account held on a day.
  'money_account_statement',
  // 0168: medicines counted before their batch and expiry were recorded.
  'pharmacy_counted_stock',
  // 0171: an investor partnership's earning for a vehicle-week.
  'investor_earning',
] as const;
export type AuditEntity = (typeof AUDIT_ENTITIES)[number];

// ── Excel / CSV import ──────────────────────────────────────────────────────

/**
 * What a workbook is being imported *as*.
 *
 * Deliberately not "a spreadsheet importer" in the abstract. Each dataset
 * lands in the tables the product already uses — `sales` rows become ordinary
 * sales, `products` rows become ordinary catalogue items with an opening
 * balance in the ordinary stock ledger — so an imported record is
 * indistinguishable from a typed one everywhere downstream. An importer that
 * wrote into tables of its own would be a second system of record, which is
 * the thing this migration exists to get rid of.
 */
/**
 * Whether CenoX watched a delivery happen, or read it afterwards.
 *
 * Both are real records of real jobs. Only one of them has a live counterpart
 * — a rider, a dispatch job, an outbox event — and code that decides whether
 * to notify, emit or pay needs to be able to ask.
 */
/**
 * A rider's standing in the workspace. `inactive` keeps every historical
 * delivery and settlement intact — a rider who left still earned what they
 * earned, and a report for last March must still name them.
 */
/**
 * Where a rider record came from.
 *
 * `import` means derived from a workbook, with no identifier of its own — the
 * seven riders who exist only in the June–July books. It governs nothing; it is
 * evidence for the identity questions that come later.
 */
/**
 * How a commission rate is expressed.
 *
 * `fixed` is naira per delivery — everything the Shop has ever done. `percentage`
 * is a share of the fee, which is what the App charges: exactly 10% on all 13 of
 * its rows in the staging workbook. Explicit rather than inferred from the
 * magnitude, because "under 100 means a percentage" breaks the first time a
 * rider is on ₦50 a drop.
 */
/**
 * Where a settlement with a rider stands.
 *
 * The staging workbook has 254 settlements and every one is `UNPAID` with no
 * Date Paid — the paid state exists as columns there and has never been used.
 */
export const SETTLEMENT_STATUSES = ['unpaid', 'paid', 'void'] as const;
export type SettlementStatus = (typeof SETTLEMENT_STATUSES)[number];

export const SETTLEMENT_STATUS_LABELS: Record<SettlementStatus, string> = {
  unpaid: 'Unpaid',
  paid: 'Paid',
  void: 'Void',
};

export const RATE_TYPES = ['fixed', 'percentage'] as const;
export type RateType = (typeof RATE_TYPES)[number];

export const RATE_TYPE_LABELS: Record<RateType, string> = {
  fixed: 'Fixed amount per delivery',
  percentage: 'Percentage of the delivery fee',
};

export const RIDER_ORIGINS = ['manual', 'import'] as const;
export type RiderOrigin = (typeof RIDER_ORIGINS)[number];

/**
 * Where a rider stands with the company.
 *
 * `suspended` and `inactive` are deliberately different. A suspended rider is
 * expected back — stood down over a dispute, off with an injury — and the
 * arrangement still exists. A former rider has left. Collapsing them makes
 * "how many riders do we have" unanswerable, which is the question the
 * directory exists to answer.
 */
export const RIDER_STATUSES = ['active', 'suspended', 'inactive'] as const;
export type RiderStatus = (typeof RIDER_STATUSES)[number];

export const RIDER_STATUS_LABELS: Record<RiderStatus, string> = {
  active: 'Active',
  suspended: 'Suspended',
  inactive: 'Archived',
};

/**
 * Where a weekly remittance stands.
 *
 * `waived` is an exempt rider: considered and excluded, which is a different
 * fact from `unpaid` and from having no row at all. The settlement sheet shows
 * all three differently, because a blank cannot distinguish "exempt" from
 * "forgotten".
 */
export const REMITTANCE_STATUSES = ['unpaid', 'paid', 'waived'] as const;
export type RemittanceStatus = (typeof REMITTANCE_STATUSES)[number];

export const REMITTANCE_STATUS_LABELS: Record<RemittanceStatus, string> = {
  unpaid: 'Unpaid',
  paid: 'Paid',
  waived: 'Waived (exempt)',
};

/**
 * How an expense came to exist.
 *
 * `manual` is a person: they typed it, or they pressed Record on a recurring
 * one, and `recordedByUserId` names them. `recurring_schedule` is the nightly
 * sweep acting on a decision somebody took once, when they switched auto-create
 * on — so nobody is named, because nobody did anything that day.
 */
export const EXPENSE_SOURCES = ['manual', 'recurring_schedule'] as const;
export type ExpenseSource = (typeof EXPENSE_SOURCES)[number];

export const EXPENSE_SOURCE_LABELS: Record<ExpenseSource, string> = {
  manual: 'Recorded by a person',
  recurring_schedule: 'Generated from a schedule',
};

/**
 * How often a recurring expense comes round.
 *
 * `custom` carries its own interval in days, for the arrangements that do not
 * fit a calendar word — a generator serviced every 45 days, rent paid every
 * ten weeks.
 */
export const EXPENSE_FREQUENCIES = ['daily', 'weekly', 'monthly', 'yearly', 'custom'] as const;
export type ExpenseFrequency = (typeof EXPENSE_FREQUENCIES)[number];

export const EXPENSE_FREQUENCY_LABELS: Record<ExpenseFrequency, string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
  yearly: 'Yearly',
  custom: 'Custom',
};

/**
 * Where a recurring expense schedule stands.
 *
 * `paused` and `stopped` are different facts and the difference matters at the
 * end of the year: a paused schedule is coming back, so the arrangement still
 * exists; a stopped one has ended. Collapsing them would make "how many
 * standing costs does this business carry" unanswerable.
 */
export const EXPENSE_SCHEDULE_STATUSES = ['active', 'paused', 'stopped'] as const;
export type ExpenseScheduleStatus = (typeof EXPENSE_SCHEDULE_STATUSES)[number];

export const EXPENSE_SCHEDULE_STATUS_LABELS: Record<ExpenseScheduleStatus, string> = {
  active: 'Active',
  paused: 'Paused',
  stopped: 'Stopped',
};

/**
 * Where a weekly remittance stands, as a READER sees it.
 *
 * Derived, never stored. `REMITTANCE_STATUSES` above is the stored column, and
 * it records the two things somebody DECIDED — settled, or waived. Everything
 * else here is a question about two numbers and today's date:
 *
 *   waived          somebody excused it
 *   paid            the ledger covers the accrued amount
 *   partially_paid  some money arrived, a balance remains
 *   overdue         a balance remains and the due date has passed
 *   due             a balance remains and it has not
 *
 * Storing this is the classic bug: a row that was `overdue` at midnight stays
 * `overdue` after it is paid until something remembers to rewrite it, and the
 * thing that forgets is always the path nobody tested.
 */
export const REMITTANCE_STATES = ['due', 'partially_paid', 'paid', 'overdue', 'waived'] as const;
export type RemittanceState = (typeof REMITTANCE_STATES)[number];

export const REMITTANCE_STATE_LABELS: Record<RemittanceState, string> = {
  due: 'Due',
  partially_paid: 'Partially paid',
  paid: 'Paid',
  overdue: 'Overdue',
  waived: 'Waived',
};

/**
 * What kind of vehicle it is.
 *
 * The register is still the `bikes` table, because every agreement, every
 * assignment and every accrued week references it by id — renaming it would
 * buy a nicer label at the cost of real data. The category is what turns a
 * bike register into a fleet register, and everything already on the books is
 * a motorcycle.
 */
export const VEHICLE_CATEGORIES = [
  'motorcycle',
  'car',
  'van',
  'truck',
  'bus',
  'tricycle',
] as const;
export type VehicleCategory = (typeof VEHICLE_CATEGORIES)[number];

export const VEHICLE_CATEGORY_LABELS: Record<VehicleCategory, string> = {
  motorcycle: 'Motorcycle / Bike',
  car: 'Car',
  van: 'Van',
  truck: 'Truck',
  bus: 'Bus',
  tricycle: 'Tricycle / Keke',
};

/**
 * How often a remittance falls due.
 *
 * `per_trip` is the odd one, and the reason this is stored rather than assumed:
 * a truck charged per trip has no standing weekly obligation, so a period
 * accrues nothing for it and the amount is owed against trips instead. Accrual
 * therefore has to be able to tell the difference.
 */
export const REMITTANCE_FREQUENCIES = ['daily', 'weekly', 'monthly', 'per_trip'] as const;
export type RemittanceFrequency = (typeof REMITTANCE_FREQUENCIES)[number];

export const REMITTANCE_FREQUENCY_LABELS: Record<RemittanceFrequency, string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
  per_trip: 'Per trip',
};

/**
 * Who owns a bike.
 *
 * Not the same question as who is riding it. An investor's bike on a rider's
 * hire purchase agreement is owned by the investor, held by the rider, and
 * being bought by the rider — three facts that a single column would lose.
 */
export const BIKE_OWNERSHIPS = ['cenohub', 'investor', 'rider', 'other'] as const;
export type BikeOwnership = (typeof BIKE_OWNERSHIPS)[number];

export const BIKE_OWNERSHIP_LABELS: Record<BikeOwnership, string> = {
  cenohub: 'Company',
  investor: 'Investor',
  rider: 'Rider',
  other: 'Other',
};

/**
 * What a person picks when registering a bike. `other` stays readable for
 * bikes recorded before ownership decided anything, and is priced as the
 * company's.
 */
export const SELECTABLE_BIKE_OWNERSHIPS = ['cenohub', 'investor', 'rider'] as const;

/** The investor's weekly share of a remittance, unless an agreement says otherwise. */
export const DEFAULT_INVESTOR_WEEKLY_ENTITLEMENT = '10000.00';

/** Where a bike is in its life. */
export const BIKE_STATUSES = [
  'active',
  'spare',
  'under_repair',
  'hire_purchase',
  'repossessed',
  'sold',
] as const;
export type BikeStatus = (typeof BIKE_STATUSES)[number];

export const BIKE_STATUS_LABELS: Record<BikeStatus, string> = {
  active: 'Active',
  spare: 'Spare',
  under_repair: 'Under repair',
  hire_purchase: 'On hire purchase',
  repossessed: 'Repossessed',
  sold: 'Sold',
};

/** Bikes that are on the road, for the register's own summary. */
export const BIKE_IN_SERVICE_STATUSES: readonly BikeStatus[] = ['active', 'hire_purchase'];

/**
 * Where a hire purchase agreement stands.
 *
 * Four decisions, and `overdue` is deliberately not among them — whether a
 * rider is behind is arithmetic on the schedule and today's date, for the same
 * reason the remittance state is derived.
 */
export const HIRE_PURCHASE_STATUSES = [
  'active',
  'completed',
  'suspended',
  'repossessed',
] as const;
export type HirePurchaseStatus = (typeof HIRE_PURCHASE_STATUSES)[number];

export const HIRE_PURCHASE_STATUS_LABELS: Record<HirePurchaseStatus, string> = {
  active: 'Active',
  completed: 'Completed',
  suspended: 'Suspended',
  repossessed: 'Repossessed',
};

/** An agreement that still holds the bike — see the one-live-per-bike index. */
export const HIRE_PURCHASE_LIVE_STATUSES: readonly HirePurchaseStatus[] = [
  'active',
  'suspended',
];

export const INSTALMENT_FREQUENCIES = ['daily', 'weekly'] as const;
export type InstalmentFrequency = (typeof INSTALMENT_FREQUENCIES)[number];

export const INSTALMENT_FREQUENCY_LABELS: Record<InstalmentFrequency, string> = {
  daily: 'Daily',
  weekly: 'Weekly',
};

/** Days per instalment, for projecting a completion date. */
export const INSTALMENT_FREQUENCY_DAYS: Record<InstalmentFrequency, number> = {
  daily: 1,
  weekly: 7,
};

/**
 * Why a delivery's arithmetic needed a judgement call.
 *
 * `zero_fee` is the one the historical records contain: a fare of ₦0, which the
 * workbook charges ₦500 commission on and reports as the rider earning −₦500.
 * CenoX books ₦0 and ₦0 and records the divergence here, so the difference from
 * the source is explainable rather than merely absent.
 */
export const DELIVERY_ANOMALY_CODES = ['zero_fee', 'fee_below_rate', 'payout_above_fee'] as const;
export type DeliveryAnomalyCode = (typeof DELIVERY_ANOMALY_CODES)[number];

export const DELIVERY_ANOMALY_LABELS: Record<DeliveryAnomalyCode, string> = {
  zero_fee: 'Zero fare',
  fee_below_rate: 'Fare below the commission rate',
  payout_above_fee: 'Payout above the fare',
};

/**
 * Payment spellings the historical records use for cash, and what they mean.
 *
 * Offered as defaults for the approval screen, never applied on their own: the
 * mapping that normalises them is a row in `payment_method_aliases` with an
 * approver attached. The workbook's own `COUNTIF(H,"CASH")` matches only the
 * first of these and drops the other 84 deliveries from both the cash and the
 * transfer column.
 */
export const PAYMENT_METHOD_ALIAS_SUGGESTIONS: ReadonlyArray<{
  alias: string;
  canonical: string;
}> = [
  { alias: 'CASH', canonical: 'cash' },
  { alias: 'CASH TO HIM', canonical: 'cash' },
  { alias: 'TO HIM', canonical: 'cash' },
  { alias: 'CASH{ TO HIM}', canonical: 'cash' },
  { alias: 'TO HIM CASH', canonical: 'cash' },
  { alias: 'TRANSFER', canonical: 'bank_transfer' },
];

/** Logistics report grains. Remittance is period-granular; see LOGISTICS_MEASURES. */
export const LOGISTICS_GRAINS = ['day', 'week', 'month', 'year'] as const;
export type LogisticsGrain = (typeof LOGISTICS_GRAINS)[number];

/** What a logistics report can be broken down by. */
/**
 * What a Logistics report can be broken down by.
 *
 * `source` and `origin` are different questions and both get asked. `source` is
 * the channel the job came through — Shop, App, WhatsApp — which is what decides
 * its commission rate. `origin` is how the record reached CenoX: watched
 * happening, or read from a sheet. Collapsing them would make "how much does the
 * App bring in" unanswerable.
 */
export const LOGISTICS_DIMENSIONS = ['rider', 'location', 'status', 'source', 'origin'] as const;
export type LogisticsDimension = (typeof LOGISTICS_DIMENSIONS)[number];

export const DELIVERY_RECORD_ORIGINS = ['live', 'import'] as const;
export type DeliveryRecordOrigin = (typeof DELIVERY_RECORD_ORIGINS)[number];

export const IMPORT_DATASETS = [
  'sales',
  'products',
  'customers',
  'stock',
  /**
   * Historical deliveries, read from a spreadsheet after the fact.
   *
   * Real records of real jobs — they belong in the books and on the same
   * timeline as everything else. What they are not is deliveries CenoX watched
   * happen, so they carry `record_origin = 'import'` and never travel the live
   * posting path. See `modules/imports/deliveries.ts`.
   */
  'deliveries',
] as const;
export type ImportDataset = (typeof IMPORT_DATASETS)[number];

export const IMPORT_DATASET_LABELS: Record<ImportDataset, string> = {
  sales: 'Sales history',
  products: 'Products & opening stock',
  customers: 'Customers',
  stock: 'Stock movements',
  deliveries: 'Delivery history',
};

/**
 * The life of a batch.
 *
 * `preview` writes nothing to the business's own tables — it exists so the
 * counts and the failures can be read before anything is committed. That
 * separation is the whole point: a migration that reports its damage
 * afterwards is not a migration anyone can trust with three years of records.
 */
export const IMPORT_STATUSES = ['preview', 'committed', 'rolled_back', 'discarded'] as const;
export type ImportStatus = (typeof IMPORT_STATUSES)[number];

export const IMPORT_STATUS_LABELS: Record<ImportStatus, string> = {
  preview: 'Awaiting review',
  committed: 'Imported',
  rolled_back: 'Rolled back',
  discarded: 'Discarded',
};

/**
 * What happened to one row.
 *
 * `duplicate` is a first-class outcome, not an error. Re-uploading the same
 * workbook must be safe and boring: every row comes back `duplicate`, nothing
 * is written twice, and the counts say so plainly.
 */
export const IMPORT_ROW_STATUSES = [
  'pending',
  'imported',
  'duplicate',
  'failed',
  'skipped',
  'rolled_back',
] as const;
export type ImportRowStatus = (typeof IMPORT_ROW_STATUSES)[number];

export const IMPORT_ROW_STATUS_LABELS: Record<ImportRowStatus, string> = {
  pending: 'Ready to import',
  imported: 'Imported',
  duplicate: 'Already in CenoX',
  failed: 'Could not import',
  skipped: 'Skipped',
  rolled_back: 'Rolled back',
};

// ── Commercial layer: modules, plans, metering ──────────────────────────────

/**
 * The switchable capabilities of the platform.
 *
 * A module key is a *stable identifier*, never a label: it appears in plan
 * entitlements, in per-business switches, in usage records and in audit rows,
 * so renaming one would rewrite history. Labels live below and may change
 * freely.
 *
 * Deliberately flat rather than nested. A business either has Delivery or it
 * does not; expressing that as a tree invites questions ("half of Delivery?")
 * that nobody has a good answer to.
 */
/**
 * The CenoX products.
 *
 * A product is something a workspace subscribes to. A MODULE is something a
 * plan within a product grants — `sales_pos` and `delivery` are modules of
 * CenoX Business, not products. The two were the same thing while Business was
 * the whole application, and keeping them the same now would mean a workspace
 * could buy "delivery" without buying Business.
 *
 * `business` is in production. `ai` is the platform's first new surface.
 * The other seven are listed and not yet sellable — which is what
 * `cenox_products.status` records, in the database, because it is a commercial
 * fact that will change without a deploy.
 */
export const PRODUCT_KEYS = [
  'ai',
  'business',
  'verify',
  'growth',
  'automation',
  'train',
  'robotics',
  'autonomous',
  'intelligence',
] as const;
export type ProductKey = (typeof PRODUCT_KEYS)[number];

/**
 * How a product's initialization is going.
 *
 * Separate from entitlement and from activation because they answer different
 * questions and can disagree. A workspace may be entitled and not yet
 * provisioned; provisioned and switched off; or half-way through a
 * provisioner that calls another system and takes a minute.
 */
export const PROVISIONING_STATUSES = ['pending', 'provisioning', 'active', 'failed'] as const;
export type ProvisioningStatus = (typeof PROVISIONING_STATUSES)[number];

/**
 * What the one button on a product card should do.
 *
 * Resolved on the server rather than derived in the browser: the rule reads
 * three facts — is the workspace entitled, is the product initialized, is the
 * product sellable — and a front end that recomputed it would eventually offer
 * "Open" for something still provisioning.
 */
/**
 * Why a product's initialization failed, in terms safe to show anyone.
 *
 * A fixed vocabulary, because the alternative is putting whatever the
 * provisioner or the database driver actually said on a product card — and
 * what those say includes connection strings, provider payloads, and the
 * fragment of SQL that failed.
 *
 * The detailed reason is kept; it is simply not the thing that leaves the
 * server. See `business_products.failure_reason` versus `failure_code`.
 */
export const PROVISIONING_FAILURE_CODES = [
  /** The product's own setup raised. Retryable. */
  'provisioner_failed',
  /** Something the product depends on was unreachable. Retryable. */
  'dependency_unavailable',
  /** The worker holding it stopped without finishing. Retryable. */
  'timed_out',
  /** Too many attempts. Retryable only after somebody looks. */
  'exhausted',
  /** Anything not yet classified. Retryable. */
  'unknown',
] as const;
export type ProvisioningFailureCode = (typeof PROVISIONING_FAILURE_CODES)[number];

/** What to tell a person. Deliberately says nothing about the cause. */
export const PROVISIONING_FAILURE_MESSAGES: Record<ProvisioningFailureCode, string> = {
  provisioner_failed: 'Setting this product up did not finish. You can try again.',
  dependency_unavailable: 'A service this product needs was unavailable. You can try again.',
  timed_out: 'Setting this product up stopped before it finished. You can try again.',
  exhausted: 'Setting this product up has failed several times. Contact support.',
  unknown: 'Setting this product up did not finish. You can try again.',
};

export const PRODUCT_ACTIONS = [
  /** Entitled, initialized, switched on. Go in. */
  'open',
  /** Entitled and never initialized. Run the provisioner. */
  'activate',
  /** Initialized but switched off, and entitled again. Turn access back on —
   *  the data is where it was left, and must not be provisioned twice. */
  'reactivate',
  /** The provisioner is running. Nothing for anyone to press. */
  'wait',
  /** The provisioner raised. Recoverable, and the reason is recorded. */
  'retry',
  /** It has failed too many times to be a button. Somebody has to look. */
  'contact_support',
  /** Not entitled, sellable, and a trial exists. */
  'start_trial',
  /** Not entitled, sellable, no trial. */
  'upgrade',
  /** Listed but not sold yet. Says so; exposes nothing. */
  'unavailable',
] as const;
export type ProductAction = (typeof PRODUCT_ACTIONS)[number];

export const PRODUCT_LABELS: Record<ProductKey, string> = {
  ai: 'CenoX AI',
  business: 'CenoX Business',
  verify: 'CenoX Verify',
  growth: 'CenoX Growth',
  automation: 'CenoX Automation',
  train: 'CenoX Train & Earn',
  robotics: 'CenoX Robotics',
  autonomous: 'CenoX Autonomous Systems',
  intelligence: 'CenoX Intelligence',
};

/**
 * The product every existing workspace already holds.
 *
 * Used wherever a product-scoped read needs the answer that was correct before
 * products existed. Every plan and every subscription written before the
 * platform migration belongs to it — a statement of history, not a default.
 */
export const DEFAULT_PRODUCT_KEY = 'business' satisfies ProductKey;

export const MODULE_KEYS = [
  'sales_pos',
  'inventory',
  'expenses',
  'crm',
  'delivery',
  'staff',
  'whatsapp_ai',
  'ai_assistant',
  'analytics',
  'api_integrations',
  /**
   * Rider remittance and hire purchase: the money that moves between a courier
   * and the people who ride for it.
   *
   * Its own capability, not an inference from "delivery on, inventory off".
   * That inference happens to describe Ceno Hub and would break the first
   * courier that also runs a warehouse, or the first shop that sells bikes to
   * its drivers on instalments.
   */
  'rider_finance',
  /**
   * Pharmacy: medicine catalogue with selling units, batch and expiry stock,
   * purchasing, transfers, prescriptions, patients, restricted-medicine
   * registers, storage monitoring and recalls.
   *
   * A vertical, so opt-in everywhere except a business whose type is
   * `pharmacy`. It builds on Inventory and Sales rather than replacing them:
   * a pharmacy sale is an ordinary sale, a batch movement is an ordinary
   * stock movement that also names its batch.
   */
  'pharmacy',
] as const;
export type ModuleKey = (typeof MODULE_KEYS)[number];

export const MODULE_LABELS: Record<ModuleKey, string> = {
  sales_pos: 'Sales & POS',
  inventory: 'Inventory',
  expenses: 'Expenses',
  crm: 'Customers (CRM)',
  delivery: 'Delivery',
  staff: 'Staff',
  whatsapp_ai: 'Social Media AI',
  ai_assistant: 'AI Assistant',
  analytics: 'Analytics',
  api_integrations: 'API & Integrations',
  rider_finance: 'Remittance & Hire Purchase',
  pharmacy: 'Pharmacy',
};

export const MODULE_DESCRIPTIONS: Record<ModuleKey, string> = {
  sales_pos: 'Record sales at the counter and from every channel',
  inventory: 'Stock levels, movements and reorder alerts',
  expenses: 'What the business spends, by category',
  crm: 'Customer records, history and outstanding balances',
  delivery: 'Dispatch, riders and delivery tracking',
  staff: 'Staff records, attribution and commissions',
  whatsapp_ai: 'Take orders and answer customers on social channels',
  ai_assistant: 'Ask questions about your own business data',
  analytics: 'Reports, breakdowns and business intelligence',
  api_integrations: 'API keys and third-party integrations',
  rider_finance: "Riders' weekly remittance, and bikes sold on instalments",
  pharmacy: 'Medicines, batches and expiry, prescriptions, patients and dispensing',
};

/**
 * The social channels Social Media AI speaks on, and which of them exist yet.
 *
 * The module is one capability with one key — `whatsapp_ai`, unchanged, because
 * that key is written into plan entitlements, usage rows and audit history and
 * renaming it would rewrite the past. What changed is the NAME a customer
 * reads: the feature is sold as Social Media AI, and WhatsApp is the first
 * channel it speaks on rather than the whole of it.
 *
 * Everything not `live` is shown and says so. A roadmap a customer can read is
 * worth more than a short list that implies the others were never considered —
 * and listing them here, with a status, is what stops "coming soon" from being
 * a sentence somebody typed into a component and then had to remember to
 * delete. Turning one on is a one-word edit to this table once its integration
 * exists.
 */
export const SOCIAL_AI_CHANNEL_STATUSES = ['live', 'coming_soon'] as const;
export type SocialAiChannelStatus = (typeof SOCIAL_AI_CHANNEL_STATUSES)[number];

export interface SocialAiChannel {
  key: string;
  label: string;
  status: SocialAiChannelStatus;
}

export const SOCIAL_AI_CHANNELS: readonly SocialAiChannel[] = [
  /** In production: the existing WhatsApp integration, untouched. */
  { key: 'whatsapp', label: 'WhatsApp', status: 'live' },
  { key: 'instagram', label: 'Instagram', status: 'coming_soon' },
  { key: 'facebook', label: 'Facebook Messenger', status: 'coming_soon' },
  { key: 'tiktok', label: 'TikTok', status: 'coming_soon' },
  { key: 'x', label: 'X', status: 'coming_soon' },
];

/** The channels a customer can actually use today. */
export function liveSocialAiChannels(): readonly SocialAiChannel[] {
  return SOCIAL_AI_CHANNELS.filter((c) => c.status === 'live');
}

/**
 * Modules a business cannot switch off.
 *
 * Not a commercial decision — a structural one. `analytics` is the dashboard,
 * the reports and every total in the product; a business without it has no way
 * to read itself. A plan may still gate the *rest*; this is the floor beneath
 * every plan.
 *
 * Sales used to sit here on the reasoning that every other module assumes
 * sales exist. It does not hold: a Delivery & Logistics company records
 * deliveries and weekly remittance, and never rings up a sale. Nothing in the
 * API gates on `sales_pos` — it decides whether the Sales screen is offered —
 * so a courier switching it off loses a menu item it cannot use, and keeps
 * every sale already recorded. See BUSINESS_TYPE_MODULE_DEFAULTS.
 */
/**
 * Which product owns which module.
 *
 * Every module today is a CenoX Business module, because Business was the
 * whole application. The map exists so that the first module belonging to
 * another product cannot be added without saying which — otherwise `analytics`
 * quietly means two different things in Business and Intelligence.
 */
export const MODULE_PRODUCT: Record<ModuleKey, ProductKey> = {
  sales_pos: 'business',
  inventory: 'business',
  expenses: 'business',
  crm: 'business',
  delivery: 'business',
  staff: 'business',
  whatsapp_ai: 'business',
  ai_assistant: 'business',
  analytics: 'business',
  api_integrations: 'business',
  rider_finance: 'business',
  pharmacy: 'business',
};

/** Every module this product grants. */
export function modulesOfProduct(product: ProductKey): ModuleKey[] {
  return MODULE_KEYS.filter((key) => MODULE_PRODUCT[key] === product);
}

export const CORE_MODULE_KEYS: readonly ModuleKey[] = ['analytics'];

/**
 * Modules a business must switch ON deliberately, even when its plan includes them.
 *
 * Most modules default to on: a business that upgrades should see what it paid for without
 * hunting through settings. A VERTICAL is different. Deliveries brings riders, pickup and
 * drop-off addresses and payout expenses — a vocabulary that means nothing to a supermarket, and
 * a menu item it can never use is worse than no menu item at all.
 *
 * So the plan grants the right to use it; the business decides whether it is one of those
 * businesses. Opt-in rather than opt-out, because the cost of the two mistakes is not symmetric:
 * a courier switching Deliveries on is one click, while every retail tenant on the same plan
 * silently acquiring a logistics screen is a product that stopped knowing what it is.
 */
export const OPT_IN_MODULE_KEYS: readonly ModuleKey[] = ['delivery', 'rider_finance', 'pharmacy'];

/**
 * What a business of each type starts with, before anybody touches a switch.
 *
 * The middle layer of three, and only the middle one:
 *
 *   plan entitlement            may this business use the module at all
 *   explicit switch → THIS → global fallback (OPT_IN_MODULE_KEYS)
 *                               is it on
 *   member permission           may this person use it
 *
 * Read two ways, deliberately:
 *
 *  - **Written once, at provisioning.** A new business gets these as actual
 *    `business_modules` rows — see the Business provisioner. "Every logistics
 *    company always has Rider Finance" is a statement about a category, and
 *    applying it forever to businesses that were never asked is how a courier
 *    that leases its bikes ends up carrying a capability it does not use.
 *    Persisted per workspace, it is a switch its owner can simply turn off.
 *
 *  - **Resolved on read, for everything else.** Businesses created before a
 *    key was added here have no row for it, and fall back to this table — so
 *    they still inherit a sensible answer rather than nothing.
 *
 * An explicit switch always wins over both, and turning a module off never
 * deletes anything — re-enabling it finds the data where it was left.
 *
 * Keyed by type alone. Nothing here, or anywhere downstream, may name a
 * particular business.
 */
export const BUSINESS_TYPE_MODULE_DEFAULTS: Partial<
  Record<BusinessType, Partial<Record<ModuleKey, boolean>>>
> = {
  /**
   * A delivery company: Deliveries and Logistics are the job; shelves are not.
   * Inventory — and with it Shop, Products, Receiving and Stock count — can be
   * switched on by one that also runs a warehouse or fulfilment operation.
   *
   * Sales starts OFF and Remittance & Hire Purchase ON, because that is what
   * this business's money actually is: riders settling a weekly figure and
   * paying off a bike, not a counter ringing up goods. Both are switches, not
   * facts — a courier that opens a shop turns Sales back on and keeps every
   * sale it ever recorded, and one that leases rather than finances bikes
   * turns Remittance & Hire Purchase off.
   */
  logistics: { delivery: true, inventory: false, rider_finance: true, sales_pos: false },
  /**
   * A pharmacy sells over a counter and keeps stock, so Sales and Inventory
   * stay on, and the Pharmacy module starts on. Delivery stays opt-in: it is
   * a growth feature that depends on a delivery partner being configured.
   */
  pharmacy: { pharmacy: true, inventory: true, sales_pos: true },
};

/**
 * Whether a business keeps a counter's cash: the retail money screen ("Money &
 * cash", "Money & Stock", a shop's Finance Overview) with expected cash, cash
 * counts, variances and their alerts.
 *
 * A business that sells over a counter or keeps stock does. One that runs
 * deliveries with neither Sales & POS nor Inventory — a courier — does not:
 * its money is delivery fees, rider remittance and payouts, read on Cash &
 * bank and the earnings screens, and a till reconciliation there is a screen
 * of nothing. Decided by modules, so a courier that switches Sales or
 * Inventory on gets it back with every record it ever kept. Nothing is
 * deleted either way.
 */
export function keepsCounterCash(active: Iterable<ModuleKey>): boolean {
  const on = new Set(active);
  return !on.has('delivery') || on.has('sales_pos') || on.has('inventory');
}

/**
 * Modules a business of a type holds on EVERY plan, Starter included.
 *
 * For most types the plan decides what a business may use. For some, a module
 * is the business itself: a Delivery & Logistics company that signed up and
 * could not record a delivery would have been sold nothing. So these count as
 * granted whatever the plan says, and plans differ for them through limits and
 * advanced features instead — delivery volume, riders, locations, automation.
 *
 * Granted is not forced on: the type default above still decides whether it
 * starts on, and an owner may still switch it off explicitly.
 */
export const BUSINESS_TYPE_CORE_MODULES: Partial<Record<BusinessType, readonly ModuleKey[]>> = {
  /*
    Remittance & Hire Purchase travels with Deliveries for the same reason:
    a courier whose riders settle weekly, on bikes the company financed, was
    not sold a plan that can record the deliveries but not the money they owe.
  */
  logistics: ['delivery', 'rider_finance'],
  /*
    A pharmacy that signed up and could not record a batch or a prescription
    would have been sold nothing — the same reasoning as Deliveries above.
  */
  pharmacy: ['pharmacy'],
};

/** Modules this type of business holds regardless of plan. */
export function typeCoreModules(type: string | null | undefined): readonly ModuleKey[] {
  return (type ? BUSINESS_TYPE_CORE_MODULES[type as BusinessType] : undefined) ?? [];
}

/**
 * The default for one module in one type of business, before any switch.
 * Plan entitlement is applied separately and always wins.
 */
export function moduleDefaultFor(type: string | null | undefined, key: ModuleKey): boolean {
  const byType = type ? BUSINESS_TYPE_MODULE_DEFAULTS[type as BusinessType] : undefined;
  const typed = byType?.[key];
  if (typed !== undefined) return typed;
  return !OPT_IN_MODULE_KEYS.includes(key);
}

/**
 * What gets metered.
 *
 * Two shapes, and the difference decides how each is read back:
 *
 *  - **levels** (`users`, `branches`) are a fact about right now. "Three users"
 *    is true until someone is added; it is not a thing that accumulates.
 *  - **counters** (everything else) accumulate within a billing period and
 *    reset when it rolls over.
 *
 * Conflating them is the classic metering bug: a business that adds and removes
 * a user twice would show four users against a limit of three.
 */
export const USAGE_METRICS = [
  'users',
  'branches',
  'transactions',
  'storage_bytes',
  'ai_tokens',
  'api_calls',
  'whatsapp_messages',
  'deliveries',
] as const;
export type UsageMetric = (typeof USAGE_METRICS)[number];

export const USAGE_METRIC_KIND: Record<UsageMetric, 'level' | 'counter'> = {
  users: 'level',
  branches: 'level',
  transactions: 'counter',
  /*
    A level, not a counter.

    Storage is how much is held right now — delete a file and it goes down —
    and it was declared a counter, which meant it would have accumulated
    within a period and reset at the start of the next one. The screen would
    have read "2 GB of storage used this month", a sentence about storage that
    is not true of storage. Nothing recorded it, so nothing was ever wrong on
    screen; the declaration was.
  */
  storage_bytes: 'level',
  ai_tokens: 'counter',
  api_calls: 'counter',
  whatsapp_messages: 'counter',
  deliveries: 'counter',
};

export const USAGE_METRIC_LABELS: Record<UsageMetric, string> = {
  users: 'Team members',
  branches: 'Branches',
  transactions: 'Transactions',
  storage_bytes: 'Storage',
  ai_tokens: 'AI usage',
  api_calls: 'API calls',
  whatsapp_messages: 'WhatsApp messages',
  deliveries: 'Deliveries',
};

/**
 * Where a usage bar changes what it says.
 *
 * Three steps rather than a single "you are near your limit", because the three
 * mean different things to the person reading them: 80% is information in time
 * to plan, 95% is a decision to make this week, and 100% means capacity is used
 * and the next thing recorded goes over.
 *
 * None of them refuses anything. A transaction allowance that stopped a
 * delivery being recorded would cost the customer their record of a job they
 * already did, which is worth more than the overage it protects — see the
 * `warn` enforcement note in plan_limits.
 */
export const USAGE_THRESHOLDS = [80, 95, 100] as const;
export type UsageThreshold = (typeof USAGE_THRESHOLDS)[number];

/** The highest threshold this percentage has reached, or null below all of them. */
export function thresholdFor(percentUsed: number | null): UsageThreshold | null {
  if (percentUsed === null) return null;
  // Highest first: 97% has passed 95 and 80, and the honest answer is 95.
  for (const step of [...USAGE_THRESHOLDS].reverse()) {
    if (percentUsed >= step) return step;
  }
  return null;
}

/** Where a subscription is in its life. */
export const SUBSCRIPTION_STATUSES = [
  'trialing',
  'active',
  'past_due',
  'canceled',
  'expired',
] as const;
export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUSES)[number];

export const SUBSCRIPTION_STATUS_LABELS: Record<SubscriptionStatus, string> = {
  trialing: 'Trial',
  active: 'Active',
  past_due: 'Payment overdue',
  canceled: 'Cancelled',
  expired: 'Expired',
};

/** How often a plan bills. */
export const BILLING_INTERVALS = ['monthly', 'yearly'] as const;
export type BillingInterval = (typeof BILLING_INTERVALS)[number];

/**
 * What a tenant export or erasure is doing.
 *
 * Erasure is deliberately a tracked job rather than a DELETE: it is
 * irreversible, it must be auditable after the data it describes is gone, and
 * somebody will ask "when exactly did we delete them, and who asked".
 */
export const TENANT_JOB_KINDS = ['export', 'erasure'] as const;
export type TenantJobKind = (typeof TENANT_JOB_KINDS)[number];

export const TENANT_JOB_STATUSES = [
  'pending',
  'running',
  'completed',
  'failed',
  'cancelled',
] as const;
export type TenantJobStatus = (typeof TENANT_JOB_STATUSES)[number];

// ── Billing ─────────────────────────────────────────────────────────────────

/**
 * An invoice's life.
 *
 * `open` is the important one: an invoice exists and is owed, and **the
 * subscription has not changed**. Entitlements move on `paid`, never on
 * `open`. That single rule is what stops a plan change from being a way to get
 * paid features for nothing.
 */
export const INVOICE_STATUSES = ['draft', 'open', 'paid', 'void', 'uncollectible'] as const;
export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];

export const INVOICE_STATUS_LABELS: Record<InvoiceStatus, string> = {
  draft: 'Draft',
  open: 'Awaiting payment',
  paid: 'Paid',
  void: 'Cancelled',
  uncollectible: 'Written off',
};

export const PAYMENT_ATTEMPT_STATUSES = ['pending', 'succeeded', 'failed', 'refunded'] as const;
export type PaymentAttemptStatus = (typeof PAYMENT_ATTEMPT_STATUSES)[number];

/**
 * Which limits apply.
 *
 * A trial is not a free Business subscription — it is a demonstration, and a
 * demonstration that includes unmetered AI and WhatsApp is one where CenoX
 * pays Meta and a model provider for everybody who signs up to look around.
 * So a plan may carry two sets of limits for the same metric, and which one
 * applies is decided by whether the subscription is trialing.
 */
export const LIMIT_SCOPES = ['paid', 'trial'] as const;
export type LimitScope = (typeof LIMIT_SCOPES)[number];
